import { Component, Inject, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ARService } from '../../ar/ar.service';
import { ArCardPaymentComponent } from '../../ar/payment/ar-card-payment/ar-card-payment.component';
import { TDO_PAYMENT_VALPAY_MODE } from '@tdo-inc/tdo-payment';
import { DataARLedgerListModel, isPayableLedgerItem } from '../../ar/models/data-ar-ledger-list.model';
import { SavePaymentDistributionsModel } from '../../ar/models/save-payment-distributions.model';
import { ConversionUtilities } from 'src/app/utilities/conversion.utilities';
import { Constants } from 'src/app/constants';

export type PatientPortalPayNowDialogData = {
  patientId: string;
  patientName: string;
  totalDue: number;
  ledgerItems?: DataARLedgerListModel[];
  locId?: number;
  arAccountId?: string;
};

@Component({
  standalone: false,
  selector: 'app-patient-portal-pay-now-dialog',
  templateUrl: './patient-portal-pay-now-dialog.component.html',
  styleUrls: ['./patient-portal-pay-now-dialog.component.css'],
  providers: [CurrencyPipe],
})
export class PatientPortalPayNowDialogComponent implements OnInit {
  patientName: string = '';
  totalDue: number = 0;
  locId: number | null = null;
  arAccountId: string | null = null;
  ledgerItems: DataARLedgerListModel[] = [];

  private destroyed = false;

  amountToPay: number = 0;
  amountText: string = '';
  isPaying: boolean = false;
  isAuthorised: boolean = false;
  authorisedMessage: string = '';
  surchargeAmount: number = 0;
  errorMessage: string = '';
  lastPaymentResult: any = null;


  constructor(
    private readonly dialogRef: MatDialogRef<PatientPortalPayNowDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: PatientPortalPayNowDialogData,
    private readonly currencyPipe: CurrencyPipe,
    private readonly arService: ARService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {
    destroyRef.onDestroy(() => { this.destroyed = true; });
  }

  ngOnInit(): void {
    this.patientName = this.data?.patientName || '';
    const hasLedgerItems = Array.isArray(this.data?.ledgerItems);
    // only rows a payment can be distributed to, so the amount charged never exceeds what gets applied
    this.ledgerItems = hasLedgerItems ? (this.data.ledgerItems as DataARLedgerListModel[]).filter(isPayableLedgerItem) : [];

    const computedTotalDue = (this.ledgerItems || []).reduce((sum, t) => {
      const amt = Number((t as any)?.AmountDuePatient || 0);
      return sum + (Number.isFinite(amt) ? amt : 0);
    }, 0);
    this.totalDue = hasLedgerItems ? computedTotalDue : Number(this.data?.totalDue || 0);

    const firstTxn = (this.ledgerItems || [])[0] as any;
    const derivedLocId = Number(firstTxn?.LOCID);
    const derivedArAccountId: string | null = firstTxn?.ARAccountID || null;

    this.locId = derivedLocId || null;
    this.arAccountId = derivedArAccountId || null;

    this.amountToPay = this.totalDue;
    this.amountText = this.formatCurrency(this.amountToPay);
  }

  onAmountFocus(): void {
    this.amountText = (Number.isFinite(this.amountToPay) ? this.amountToPay : 0).toFixed(2);
  }

  onAmountKeypress(event: KeyboardEvent): boolean {
    const char = event.key;
    if (/[0-9]/.test(char)) {
      return true;
    }
    if (char === '.' && !this.amountText.includes('.')) {
      return true;
    }
    event.preventDefault();
    return false;
  }

  get isAmountEmpty(): boolean {
    return !this.amountText || this.amountText.trim().length === 0;
  }

  get isAmountBelowMinimum(): boolean {
    if (this.isAmountEmpty) return false;
    const parsed = this.parseCurrency(this.amountText);
    return parsed < 0.01;
  }

  get isAmountExceedsTotalDue(): boolean {
    if (this.isAmountEmpty) return false;
    const parsed = this.parseCurrency(this.amountText);
    return parsed > this.totalDue;
  }

  get isPaymentInvalid(): boolean {
    return this.isAmountEmpty || this.isAmountBelowMinimum || this.isAmountExceedsTotalDue;
  }

  onAmountBlur(): void {
    const parsed = this.parseCurrency(this.amountText);
    this.amountToPay = Math.min(parsed, Number.isFinite(this.totalDue) ? this.totalDue : parsed);
    this.amountText = this.formatCurrency(this.amountToPay);
  }

  async payNow(): Promise<void> {
    if (this.isPaying || this.isAuthorised) {
      return;
    }

    this.onAmountBlur();

    if (!this.data?.patientId) {
      return;
    }

    //may need to be changed to payment format needed
    if (!Number.isFinite(this.amountToPay) || this.amountToPay <= 0) {
      return;
    }

    if (!this.arAccountId || !this.locId) {
      return;
    }

    this.isPaying = true;
    if (!this.destroyed) { this.cdr.detectChanges(); }
    let arPayment: any = null;
    let openedPaymentDialog = false;
    try {
      arPayment = await firstValueFrom(this.arService.patientPortalPayment({
        patientId: this.data.patientId,
        arAccountId: this.arAccountId,
        locId: this.locId,
        amountDue: this.amountToPay
      }));

      if (!arPayment?.PaymetnID) {
        return;
      }

      const paymentProcessorID = arPayment?.PaymentProcessorId;
      if (!paymentProcessorID || paymentProcessorID === Constants.emptyGuid) {
        this.isPaying = false;
        this.errorMessage = 'Payment processor not found. Please contact the office.';        
        if (!this.destroyed) { this.cdr.detectChanges(); }
        return;
      }

      const dialogRef = this.dialog.open(ArCardPaymentComponent, {
        disableClose: true,
        panelClass: 'dialog',
        width: '95vw',
        height: '95vh',
        maxWidth: '650px',
        maxHeight: '600px',
        data: {
          paymentAmount: this.amountToPay,
          LOCID: this.locId,
          arPayment,
          paymentFrom: this.patientName,
          accountDescription: 'Patient Portal Payment',
          paymentProcessorID,
          savePaymentInfo: 0,
          valPayMode: TDO_PAYMENT_VALPAY_MODE.Online,
          countryCode: 'US',
        },
      });
      openedPaymentDialog = true;

      dialogRef.afterOpened().subscribe(() => {
        this.isPaying = false;
        if (!this.destroyed) { this.cdr.detectChanges(); }
      });

      dialogRef.afterClosed().subscribe((result) => {
        // result comes from ArCardPaymentComponent
        const resultCode = result?.data?.resultCode;
        if (result?.success && resultCode === 'Authorised') {
          // console.log('payment authorised: ', result);
          this.isAuthorised = true;
          this.authorisedMessage = 'Payment accepted. A receipt will be sent to you shortly.';
          this.surchargeAmount = result.data.surchargeAmount ?? 0;
          this.errorMessage = '';
          this.lastPaymentResult = result;
          this.dialogRef.disableClose = true;
          this.totalDue = this.totalDue - this.amountToPay;
          if (!this.destroyed) { this.cdr.detectChanges(); }
          //distribute payment across outstanding ledger items.
          this.finalizeDistributions(arPayment).catch((e) => {
            console.error('Failed to distribute patient portal payment', e);
            this.errorMessage = 'Payment was authorised, but we were unable to apply it to your ledger automatically. Please contact the office.';
            if (!this.destroyed) { this.cdr.detectChanges(); }
          });
          return;
        }
        //payment was not authorised
        const reason = result.message;
        if (!reason.includes('Transaction cancelled')) {
          this.errorMessage = `Payment was not accepted ${reason}. Please try again or click Close.`;
        }
        this.lastPaymentResult = result;
        this.isPaying = false;
        if (!this.destroyed) { this.cdr.detectChanges(); }
      });
    } catch (e) {
      console.error('Failed to set payment info', e);
      this.errorMessage = 'An error occurred while processing your payment. Please try again or contact the office.';
      this.isPaying = false;
      if (!this.destroyed) { this.cdr.detectChanges(); }
    }
     finally {
      if (!openedPaymentDialog) {
        this.isPaying = false;
      }
    }
  }

  private async finalizeDistributions(arPayment: any): Promise<void> {
    if (!arPayment?.PaymetnID) {
      return;
    }

    //sort
    const ledgerToApply = (this.ledgerItems || [])
      .filter(li => li && li.LedgerID && li.AmountDuePatient > 0 && li.isShowOnLedger === true)
      .slice()
      .sort((a, b) => {
        const da = new Date((a as any).PostDate || 0).getTime();
        const db = new Date((b as any).PostDate || 0).getTime();
        return da - db;
      });

    const distributed = this.autoApplyRemaining(ledgerToApply, this.amountToPay);
    const itemsWithApply = distributed.filter(i => Number(i.ApplyPayment) > 0);

    const saveModel = new SavePaymentDistributionsModel();
    saveModel.PaymentID = arPayment.PaymetnID;
    saveModel.ClientPostDate = new Date();
    saveModel.LOCID = this.locId;
    saveModel.dataARLedgerItems = itemsWithApply;

    await firstValueFrom(this.arService.savePaymentDistributions(saveModel));

    // After a successful distribution, send an email receipt
    try {
      arPayment.SurchargeAmount = this.surchargeAmount;
      await firstValueFrom(this.arService.sendPatientPortalPaymentReceiptEmail(arPayment, this.totalDue));
      this.authorisedMessage = 'Payment accepted. A receipt has been emailed to you.';
      if (!this.destroyed) { this.cdr.detectChanges(); }
    } catch (e) {
      console.error('Failed to send receipt email', e);
      this.authorisedMessage = 'Payment authorised. We were unable to email your receipt automatically. Please contact the office if you need a copy.';
      if (!this.destroyed) { this.cdr.detectChanges(); }
    }
  }

  private autoApplyRemaining(ledgerItems: DataARLedgerListModel[], amountToApply: number): DataARLedgerListModel[] {
    let remaining = ConversionUtilities.roundDecimal(Number(amountToApply || 0));
    if (!Number.isFinite(remaining) || remaining <= 0) {
      return ledgerItems || [];
    }

    for (const item of ledgerItems || []) {
      item.ApplyPayment = 0;

      if (remaining <= 0) {
        break;
      }

      if (!isPayableLedgerItem(item)) {
        continue;
      }

      const due = ConversionUtilities.roundDecimal(Number(item.AmountDuePatient || 0));
      if (!Number.isFinite(due) || due <= 0) {
        continue;
      }

      const apply = due <= remaining ? due : remaining;
      item.ApplyPayment = ConversionUtilities.roundDecimal(apply);
      remaining = ConversionUtilities.roundDecimal(remaining - apply);
    }

    return ledgerItems;
  }

  private formatCurrency(value: number): string {
    const safe = Number.isFinite(value) ? value : 0;
    return this.currencyPipe.transform(safe, 'USD', 'symbol', '1.2-2') || '$0.00';
  }

  private parseCurrency(value: string): number {
    const cleaned = (value || '').replace(/[^0-9.-]/g, '');
    const num = Number.parseFloat(cleaned);
    return Number.isFinite(num) && num >= 0 ? num : 0;
  }

  close(): void {
    this.dialogRef.close();
  }
}

